import { AlertTriangle, MinusCircle, PlusCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import _ from 'underscore';
import { randomUUID } from 'utils/random';

import {
  GridActionsCellItem,
  GridColDef,
  GridPreProcessEditCellProps,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid';

import { Box, Tooltip, useTheme } from '@mui/material';
import DataGrid from 'components/data-grid';
import DataGridAutocomplete, { Option } from 'components/data-grid/data-grid-autocomplete';
import { renderDataGridOption } from 'components/data-grid/render-data-grid-option';
import FlexBox from 'components/flex-box';

type Row = {
  id: string;
  fromWeight: number;
  product: { id: string; label: string };
};

type FeedStrategies = {
  fromWeight: number;
  product: { id: string; label: string | undefined };
};

type FeedStrategiesGridProps = {
  productLines: Option[];
  rows: GridRowsProp<Row>;
  handleEditing: () => void;
  feedStrategy?: FeedStrategies[];
  errorDictionary: { [key: string]: boolean };
  setRows: React.Dispatch<React.SetStateAction<readonly Row[]>>;
  changeErrorDictionary: (errorDict: { [key: string]: boolean }) => void;
};

const DEFAULT_ROW: Row = {
  id: randomUUID(),
  fromWeight: 0,
  product: { id: '', label: '' },
};

const FeedStrategiesGrid: React.FC<FeedStrategiesGridProps> = ({
  rows,
  setRows,
  productLines,
  feedStrategy,
  handleEditing,
  errorDictionary,
  changeErrorDictionary,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  // #region HANDLERS
  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows((prevRows) => _.sortBy(prevRows.map((row) => (row.id === newRow.id ? updatedRow : row)), (row) => row.fromWeight));
    return updatedRow;
  };

  const handleRowInsert = (rowId: string) => {
    const newRow: Row = { id: randomUUID(), fromWeight: 0, product: { id: '', label: '' } };
    setRows((prevRow) => {
      const idx = prevRow.findIndex((row) => row.id === rowId);
      const newRows = [...prevRow];
      newRows.splice(idx + 1, 0, newRow);
      return newRows;
    });

    setRowModesModel((oldModel) => ({ ...oldModel, [newRow.id]: { mode: GridRowModes.View } }));
  };

  const handleRowDelete = (id: string) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  // #endregion HANDLERS

  // #region RENDERS
  const renderInactiveAlert = () => {
    return (
      <Tooltip arrow title={intl.formatMessage({ id: `profilesStrategies.editFeedStrategies.productInactive` })}>
        <AlertTriangle fill={theme.palette.warning.light} color={theme.palette.darkBlue.main} size={18} style={{ marginTop: 4 }} />
      </Tooltip>
    );
  };
  // #endregion RENDERS

  useEffect(() => {
    const rows: readonly Row[] = feedStrategy?.length
      ? _.sortBy(feedStrategy, (l) => l.fromWeight).map((line) => ({
          id: randomUUID(),
          fromWeight: line.fromWeight,
          product: productLines.find((p) => p.id === line.product.id) ?? { ...line.product, label: line.product.label ?? '' },
        }))
      : [DEFAULT_ROW];
    setRows(rows);
  }, [feedStrategy, productLines]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        headerName: intl.formatMessage({ id: `profilesStrategies.label.startWeightLabel` }),
        field: 'fromWeight',
        flex: 1,
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value < 0;
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-fromWeight`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'product',
        headerName: intl.formatMessage({ id: `general.product` }),
        flex: 3,
        type: 'singleSelect',
        valueOptions: productLines,
        valueFormatter: (value: Option) => value.label,
        renderCell: renderDataGridOption,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value.id === '';
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-product`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => <DataGridAutocomplete params={params} options={productLines} />,
        sortable: false,
        editable: true,
      } as GridColDef<any, { id: number; label: string }, string>,
      {
        headerName: '',
        field: 'actions',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        type: 'actions',
        cellClassName: 'actions',
        getActions: (params) => {
          const id = params.row.id;
          const productId = params.row.product.id;
          return [
            <Box>
              <GridActionsCellItem
                label="Remove line"
                icon={<MinusCircle />}
                disabled={rows.length === 1}
                onClick={() => handleRowDelete(id)}
              />
              <GridActionsCellItem label="Add line" icon={<PlusCircle />} onClick={() => handleRowInsert(params.row.id)} />
              {productId === 'undefined' && (
                <GridActionsCellItem
                  label="warning"
                  icon={
                    <Box display={'flex'} mb={1}>
                      {renderInactiveAlert()}
                    </Box>
                  }
                />
              )}
            </Box>,
          ];
        },
      },
    ] as GridColDef[];
  }, [rows]);

  return (
    <Box width={'100%'}>
      <FlexBox>
        <Box>
          <DataGrid
            hideFooter
            autoHeight
            rows={rows}
            apiRef={apiRef}
            disableColumnMenu
            columns={columns}
            sx={{ background: 'white' }}
            getRowId={(row) => row.id}
            rowModesModel={rowModesModel}
            onCellEditStop={({ field }) => {
              if (field === 'feedId') return;
              handleEditing();
            }}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              if (field === 'product') return;
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
            processRowUpdate={processRowUpdate}
            onRowModesModelChange={handleRowModesModelChange}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          />
        </Box>
      </FlexBox>
    </Box>
  );
};

export default React.memo(FeedStrategiesGrid);
